import { authConfig } from '@/features/auth/config'

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveEndpoint(explicitValue: string | undefined, fallbackPath: string): string {
  if (explicitValue && explicitValue.trim()) {
    return explicitValue.trim()
  }

  const apiBaseUrl = authConfig.apiBaseUrl || authConfig.mainSiteUrl
  if (apiBaseUrl) {
    return `${stripTrailingSlash(apiBaseUrl)}${fallbackPath}`
  }

  return fallbackPath
}

const NVIDIA_IMAGE_ENDPOINT = resolveEndpoint(
  viteEnv.VITE_SUBSITE_NVIDIA_IMAGE_ENDPOINT,
  '/api/subsite/ai/generate-image',
)

export type AssistantImageAspectRatio = '9:16' | '16:9' | '1:1'
export type AssistantImageEditAspectRatio = 'match_input_image'

export type NvidiaGenerationModelId =
  | 'black-forest-labs/flux.2-klein-4b'
  | 'black-forest-labs/flux.1-dev'
  | 'black-forest-labs/flux.1-schnell'

export type NvidiaImageEditModelId = 'black-forest-labs/flux.1-kontext-dev'

export type NvidiaImageModelId = NvidiaGenerationModelId | NvidiaImageEditModelId

export const ASSISTANT_IMAGE_ASPECT_RATIO_OPTIONS: Array<{
  value: AssistantImageAspectRatio
  label: string
}> = [
  { value: '16:9', label: '横版 16:9' },
  { value: '9:16', label: '竖版 9:16' },
  { value: '1:1', label: '方图 1:1' },
]

export const NVIDIA_IMAGE_GENERATION_MODEL_OPTIONS = [
  { value: 'black-forest-labs/flux.2-klein-4b', label: 'FLUX.2-klein-4B' },
  { value: 'black-forest-labs/flux.1-dev', label: 'FLUX.1-dev' },
  { value: 'black-forest-labs/flux.1-schnell', label: 'FLUX.1-schnell' },
] as const

export const NVIDIA_IMAGE_EDIT_MODEL_OPTIONS = [
  { value: 'black-forest-labs/flux.1-kontext-dev', label: 'FLUX.1-Kontext-dev' },
] as const

export const DEFAULT_NVIDIA_IMAGE_MODEL: NvidiaGenerationModelId =
  'black-forest-labs/flux.2-klein-4b'

export const DEFAULT_NVIDIA_IMAGE_EDIT_MODEL: NvidiaImageEditModelId =
  'black-forest-labs/flux.1-kontext-dev'

export interface GenerateReferenceImageRequest {
  prompt: string
  aspectRatio: AssistantImageAspectRatio
  model?: NvidiaGenerationModelId
}

export interface EditReferenceImageRequest {
  prompt: string
  image: string
  sourceWidth?: number
  sourceHeight?: number
  aspectRatio?: AssistantImageEditAspectRatio
  model?: NvidiaImageEditModelId
}

export interface GeneratedReferenceImageResult {
  file: File
  width: number
  height: number
  mimeType: string
  prompt: string
  model: NvidiaImageModelId
}

interface GenerateImageResponse {
  imageBase64?: string
  mimeType?: string
  width?: number
  height?: number
  prompt?: string
  model?: string
  operation?: string
  error?: string
}

function pickImageSize(aspectRatio: AssistantImageAspectRatio): { width: number; height: number } {
  switch (aspectRatio) {
    case '9:16':
      return { width: 768, height: 1344 }
    case '16:9':
      return { width: 1344, height: 768 }
    default:
      return { width: 1024, height: 1024 }
  }
}

function inferExtension(mimeType: string): string {
  if (mimeType === 'image/png') return 'png'
  if (mimeType === 'image/webp') return 'webp'
  if (mimeType === 'image/jpeg') return 'jpg'
  return 'png'
}

function createFileFromBase64(base64: string, mimeType: string, filePrefix: string): File {
  return new File(
    [Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))],
    `${filePrefix}-${Date.now()}.${inferExtension(mimeType)}`,
    {
      type: mimeType,
    },
  )
}

export function getNvidiaImageModelLabel(model: NvidiaImageModelId): string {
  return [...NVIDIA_IMAGE_GENERATION_MODEL_OPTIONS, ...NVIDIA_IMAGE_EDIT_MODEL_OPTIONS].find(
    (option) => option.value === model,
  )?.label ?? model
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string' && reader.result.trim()) {
        resolve(reader.result)
        return
      }

      reject(new Error('无法读取图片数据。'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('无法读取图片文件。'))
    reader.readAsDataURL(file)
  })
}

export async function generateReferenceImage(
  request: GenerateReferenceImageRequest,
): Promise<GeneratedReferenceImageResult> {
  const trimmedPrompt = request.prompt.trim()
  if (!trimmedPrompt) {
    throw new Error('图片提示词不能为空。')
  }

  const size = pickImageSize(request.aspectRatio)
  const response = await fetch(NVIDIA_IMAGE_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      operation: 'generate',
      prompt: trimmedPrompt,
      width: size.width,
      height: size.height,
      model: request.model ?? DEFAULT_NVIDIA_IMAGE_MODEL,
    }),
  })

  const payload = (await response.json().catch(() => null)) as GenerateImageResponse | null
  if (!response.ok) {
    throw new Error(payload?.error || `快剪图片请求失败（${response.status}）。`)
  }

  const base64 = payload?.imageBase64?.trim()
  if (!base64) {
    throw new Error('快剪图片服务没有返回图片数据。')
  }

  const mimeType = payload?.mimeType?.trim() || 'image/png'
  const file = createFileFromBase64(base64, mimeType, 'assistant-reference')

  return {
    file,
    width: payload?.width && payload.width > 0 ? Math.round(payload.width) : size.width,
    height: payload?.height && payload.height > 0 ? Math.round(payload.height) : size.height,
    mimeType,
    prompt: payload?.prompt?.trim() || trimmedPrompt,
    model:
      (payload?.model?.trim() as NvidiaGenerationModelId | undefined) ??
      request.model ??
      DEFAULT_NVIDIA_IMAGE_MODEL,
  }
}

export async function editReferenceImage(
  request: EditReferenceImageRequest,
): Promise<GeneratedReferenceImageResult> {
  const trimmedPrompt = request.prompt.trim()
  if (!trimmedPrompt) {
    throw new Error('图片修改指令不能为空。')
  }

  if (typeof request.image !== 'string' || !request.image.startsWith('data:image/')) {
    throw new Error('图片编辑请求缺少有效图片。')
  }

  const response = await fetch(NVIDIA_IMAGE_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      operation: 'edit',
      prompt: trimmedPrompt,
      image: request.image,
      model: request.model ?? DEFAULT_NVIDIA_IMAGE_EDIT_MODEL,
      aspectRatio: request.aspectRatio ?? 'match_input_image',
      width: request.sourceWidth,
      height: request.sourceHeight,
    }),
  })

  const payload = (await response.json().catch(() => null)) as GenerateImageResponse | null
  if (!response.ok) {
    throw new Error(payload?.error || `快剪图片编辑请求失败（${response.status}）。`)
  }

  const base64 = payload?.imageBase64?.trim()
  if (!base64) {
    throw new Error('快剪图片编辑服务没有返回图片数据。')
  }

  const mimeType = payload?.mimeType?.trim() || 'image/png'
  const file = createFileFromBase64(base64, mimeType, 'assistant-image-edit')

  return {
    file,
    width:
      payload?.width && payload.width > 0
        ? Math.round(payload.width)
        : Math.max(1, Math.round(request.sourceWidth ?? 1024)),
    height:
      payload?.height && payload.height > 0
        ? Math.round(payload.height)
        : Math.max(1, Math.round(request.sourceHeight ?? 1024)),
    mimeType,
    prompt: payload?.prompt?.trim() || trimmedPrompt,
    model:
      (payload?.model?.trim() as NvidiaImageEditModelId | undefined) ??
      request.model ??
      DEFAULT_NVIDIA_IMAGE_EDIT_MODEL,
  }
}
